import { AssignmentModel, AssignmentTypeEnum, CaseTypeEnum, EventModel } from 'api/model';
import TextButton from 'components/inputs/TextButton';
import KeyValueList from 'components/KeyValueList';
import useTranslations from 'contexts/basicData/useTranslations';
import {
  assignmentFormToAssignment,
  NewAssignmentOverrides,
} from 'components/DetailedCase/DetailedCaseFormProvider/useCaseAssignmentForms';
import { FC, Suspense, useMemo } from 'react';
import styled from 'styled-components';
import { BasicFormState, ValidationResults } from 'hooks/useForm';
import FormValidations from '../FormValidations';
import useUsers from 'contexts/basicData/useUsers';
import useResultingStartAndEndTime from './useResultingStartAndEndTimes';
import useMe from 'contexts/authentication/useMe';
import useAssignmentWarningsAndMultiplePurchases from 'hooks/useAssignmentWarningsAndMultiplePurchases';
import useCaseAssignmentFormRows from '../DetailedCaseFormProvider/useCaseAssignmentFormRows';
import useEstimatedWorkTime from '../DetailedCaseFormProvider/useEstimatedWorkTime';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import {
  useDetailedCaseDataForm,
  useDetailedCaseFormControl,
} from '../DetailedCaseFormProvider';
import { CaseDataField } from '../DetailedCaseFormProvider/useCaseDataForm';
import useNewAssignmentOverrides from 'components/DetailedCase/CaseAssignments/useNewAssignmentOverrides';
import useSetSendSmsToUser from './useSetSendSmsToUser';
import useCaseAssignmentFormsEffects from './useCaseAssignmentFormEffects';
import { AssignmentField } from '../DetailedCaseFormProvider/types';
import useCompanyCoordinateResolver from '../DetailedCaseFormProvider/useCompanyCoordinateResolver';

const Wrapper = styled.div<{ isNew?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  padding-left: 10px;

  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ theme, isNew }) =>
    isNew ? theme.colors.background.highlight : 'transparent'};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const StyledKVList = styled(KeyValueList)`
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-left: 10px;
  padding-right: 5px;

  button {
    font-weight: bold;
  }
`;

interface Props {
  assignmentId: number;
  cancelable?: boolean;
  state: BasicFormState<AssignmentField>;
  validation?: ValidationResults<AssignmentField>;
  currentCaseType: CaseTypeEnum;
  companyID?: number;
  carTransportTruckID?: number;
  caseEvents: EventModel[];
  unmodifiedAssignment?: AssignmentModel;
  newAssignmentOverrides?: NewAssignmentOverrides;
  onChange(assignmentId: number, field: AssignmentField, value: string): void;
  onCancel(assignmentId: number): void;
}

const CaseAssignmentForm: FC<Props> = ({
  assignmentId,
  cancelable,
  state,
  validation,
  currentCaseType,
  companyID,
  carTransportTruckID,
  caseEvents,
  unmodifiedAssignment,
  newAssignmentOverrides,
  onChange,
  onCancel,
}) => {
  const loggedInUser = useMe();
  const { onCaseAssignmentOnHold } = useDetailedCaseContext();

  useNewAssignmentOverrides({
    assignmentId,
    newAssignmentOverrides,
    currentCaseType,
    onChange,
  });

  const { zipCodeAreas } = useTranslations();

  const usersById = useUsers();

  // Företagens geotaggar, så laddbuffertens 10 km-regel kan räknas live i formuläret
  // (annars finns koordinater bara på redan sparade uppdrag).
  const resolveCompanyCoordinates = useCompanyCoordinateResolver();
  const { state: caseState } = useDetailedCaseDataForm();
  const isElectricCase = caseState[CaseDataField.isElectric] === 'true';

  // Kontexten gör att currentAssignment bär en live-uträknad laddbuffert i stället för
  // bara den persisterade — annars räknar de härledda tiderna (estimerad arbetstid,
  // sluttid, körtidspolicykollen) med fel buffert så fort adress, län, uppdragstyp eller
  // Elbil-flaggan ändras i formuläret, och helt utan buffert på ett osparat uppdrag.
  const { savedIsElectricCase } = useDetailedCaseFormControl();
  const conversionContext = useMemo(
    () => ({ resolveCompanyCoordinates, isElectricCase, savedIsElectricCase }),
    [resolveCompanyCoordinates, isElectricCase, savedIsElectricCase]
  );

  const currentAssignment = useMemo(() => {
    return assignmentFormToAssignment(
      unmodifiedAssignment,
      state,
      usersById,
      zipCodeAreas,
      conversionContext,
      currentCaseType
    );
  }, [
    unmodifiedAssignment,
    state,
    usersById,
    zipCodeAreas,
    conversionContext,
    currentCaseType,
  ]);

  useSetSendSmsToUser({
    assignment: currentAssignment,
    currentCaseType,
    companyId: companyID,
    onChange,
  });

  const { estimatedWorkTimeMillis } = useEstimatedWorkTime(currentAssignment);

  const {
    multiplePurchasesList,
    thereAreMultiplePurchasesForAssignedUser,
    isViolatingDrivingPolicy,
  } = useAssignmentWarningsAndMultiplePurchases(
    currentCaseType,
    currentAssignment,
    true
  );

  useCaseAssignmentFormsEffects({
    currentAssignment,
    currentCaseType,
    multiplePurchasesList,
    state,
    unmodifiedAssignment,

    onChange,
    thereAreMultiplePurchasesForAssignedUser,
  });

  const { resultingStartTime, resultingEndTime } = useResultingStartAndEndTime({
    caseType: currentCaseType,
    assignmentType: Number(
      state[AssignmentField.assignmentTypeID]
    ) as AssignmentTypeEnum,
    bookedTo: state[AssignmentField.bookedTo]
      ? new Date(state[AssignmentField.bookedTo])
      : undefined,
    estimatedWorkTime: estimatedWorkTimeMillis,
    estimatedStartTime: state[AssignmentField.estimatedStartTime]
      ? new Date(state[AssignmentField.estimatedStartTime])
      : undefined,
  });

  const isNew = assignmentId < 0;

  const rows = useCaseAssignmentFormRows({
    onChange,
    currentAssignment,
    state,
    assignmentId,
    multiplePurchasesList,
    currentCaseType,
    unmodifiedAssignment,
    caseEvents,
    newAssignmentOverrides,
    resultingStartTime,
    resultingEndTime,
    loggedInUser,
    isViolatingDrivingPolicy,
    validation,
    carTransportTruckID,
    onOnHoldClick: onCaseAssignmentOnHold,
  });

  return (
    <Suspense fallback="Laddar...">
      <Wrapper isNew={isNew}>
        <Row>
          <StyledKVList colonKey rows={rows} />

          <Actions>
            {cancelable && (
              <TextButton onClick={() => onCancel(assignmentId)}>
                Avbryt
              </TextButton>
            )}
          </Actions>
        </Row>

        {validation && <FormValidations validations={validation} />}
      </Wrapper>
    </Suspense>
  );
};

export default CaseAssignmentForm;
